import type { Accept } from 'react-dropzone';
import { SourceType } from '@/types/source-types';

export const FILE_INPUT_MAX_SIZE_MB = 500;

// S3 caps a single PUT at 5 GB, which the agentic-backend enforces as exactly
// 5,000,000,000 bytes (PRESIGNED_PUT_MAX_SIZE in files.py). Large audio/video
// uploads take the presigned browser→S3 path, so this dropzone cap must not
// exceed that ceiling — otherwise files in the 5.0–5.24 GB range would pass
// client validation only to 413 at upload time. The dropzone multiplies this
// value by 1024 * 1024, so this is the largest whole MiB that stays under the
// cap: floor(5_000_000_000 / 1024 / 1024) = 4768 MiB (4,999,610,368 bytes).
export const FILE_INPUT_MEDIA_MAX_SIZE_MB = 4768;

// Mirrors the agentic-backend upload allowlist (services/upload_allowlist.py).
// Anything not listed here is rejected server-side with a 400, so keep the two
// in step. Keyed by MIME because drag-and-drop matches on the reported type;
// the extension list also matches by filename suffix.
export const FILE_INPUT_ACCEPT: Accept = {
  [SourceType.PDF]: ['.pdf'],
  [SourceType.MSWORD]: ['.doc'],
  [SourceType.MSWORDX]: ['.docx'],
  [SourceType.POWERPOINT]: ['.ppt'],
  [SourceType.POWERPOINTX]: ['.pptx'],
  [SourceType.EXCEL]: ['.xls'],
  [SourceType.SHEET]: ['.xlsx'],
  [SourceType.CSV]: ['.csv'],
  [SourceType.TSV]: ['.tsv'],
  [SourceType.TEXT]: ['.txt'],
  [SourceType.MARKDOWN]: ['.md', '.markdown'],
  [SourceType.JSON]: ['.json'],
  [SourceType.NDJSON]: ['.jsonl', '.ndjson'],
  [SourceType.PARQUET]: ['.parquet'],
  [SourceType.PNG]: ['.png'],
  [SourceType.JPEG]: ['.jpg', '.jpeg'],
  [SourceType.GIF]: ['.gif'],
  [SourceType.WEBP]: ['.webp'],
  // `.skill` bundles are ZIP on disk, so the OS usually reports them as zip.
  // When it reports octet-stream or nothing, the `.skill` suffix still matches.
  [SourceType.ZIP]: ['.zip', '.skill'],
  [SourceType.ZIP_COMPRESSED]: ['.zip', '.skill'],
  [SourceType.SKILL]: ['.skill'],
  // The backend keys on the final suffix, so its `.gz` entry covers `.tar.gz`.
  // Browsers report gzipped tarballs under any of these three MIMEs.
  [SourceType.GZIP]: ['.gz', '.tar.gz', '.tgz'],
  [SourceType.GZIP_X]: ['.gz', '.tar.gz', '.tgz'],
  [SourceType.COMPRESSED_TAR]: ['.tar.gz', '.tgz'],
  [SourceType.PYTHON]: ['.py'],
  [SourceType.JAVASCRIPT]: ['.js'],
  [SourceType.SQL]: ['.sql'],
  [SourceType.VIDEO_MP4]: ['.mp4'],
  [SourceType.AUDIO_MP4]: ['.m4a'],
  [SourceType.AUDIO_WAV]: ['.wav'],
  [SourceType.AUDIO_MPEG]: ['.mp3'],
};
